import React, { useState, useRef, useEffect } from 'react';
import musicData from '../data/music.json';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Download, Clock } from 'lucide-react';

const MusicStore = () => {
  const [filter, setFilter] = useState('all');
  const [playingTrack, setPlayingTrack] = useState(null);
  const audioRefs = useRef({});

  // Load visible tracks and sort by order
  const visibleTracks = musicData.tracks
    .filter(track => track.visible)
    .sort((a, b) => a.order - b.order);

  const filteredProducts = filter === 'all' 
    ? visibleTracks 
    : visibleTracks.filter(p => p.category === filter);

  // Handle audio playback
  const handlePlayPreview = (trackId) => {
    const currentAudio = audioRefs.current[trackId];
    
    // Pause all other tracks
    Object.entries(audioRefs.current).forEach(([id, audio]) => {
      if (parseInt(id) !== trackId && audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    if (currentAudio) {
      if (playingTrack === trackId) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setPlayingTrack(null);
      } else {
        currentAudio.play();
        setPlayingTrack(trackId);
      }
    }
  };

  // Handle audio ended event
  useEffect(() => {
    Object.entries(audioRefs.current).forEach(([id, audio]) => {
      if (audio) {
        audio.onended = () => setPlayingTrack(null);
      }
    });
  }, []);

  const handleDownload = (track) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = track.filePath;
    link.download = `${track.title}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="relative py-20 bg-zinc-900">
      {/* DJ NEFF Logo Watermark */}
      <div className="absolute inset-0 opacity-5 pointer-events-none overflow-hidden">
        <div className="flex flex-col h-full justify-around">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex justify-around whitespace-nowrap">
              {[...Array(4)].map((_, j) => (
                <img
                  key={j}
                  src="https://customer-assets.emergentagent.com/job_professional-epk/artifacts/k20ff6ft_IMG_4353_2-removebg-preview.png"
                  alt=""
                  className="w-32 h-32 md:w-40 md:h-40 object-contain"
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            MUSIC
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Exclusive mixes, remixes, and blends - Preview and download
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-3 text-sm font-bold tracking-wider transition-all duration-300 ${
              filter === 'all'
                ? 'bg-white text-black'
                : 'bg-transparent text-white border-2 border-white hover:bg-white hover:text-black'
            }`}
          >
            ALL
          </button>
          <button
            onClick={() => setFilter('mix')}
            className={`px-6 py-3 text-sm font-bold tracking-wider transition-all duration-300 ${
              filter === 'mix'
                ? 'bg-white text-black'
                : 'bg-transparent text-white border-2 border-white hover:bg-white hover:text-black'
            }`}
          >
            MIXES
          </button>
          <button
            onClick={() => setFilter('remix')}
            className={`px-6 py-3 text-sm font-bold tracking-wider transition-all duration-300 ${
              filter === 'remix'
                ? 'bg-white text-black'
                : 'bg-transparent text-white border-2 border-white hover:bg-white hover:text-black'
            }`}
          >
            REMIXES
          </button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product) => (
            <Card
              key={product.id}
              className="group overflow-hidden bg-black border-2 border-white transform hover:scale-105 transition-all duration-300"
            >
              {/* Product Image */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={product.coverArt}
                  alt={product.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                {product.isFree && (
                  <div className="absolute top-4 right-4 bg-white text-black px-3 py-1 font-bold text-sm">
                    FREE
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-6 space-y-4">
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wider">
                    {product.category}
                  </span>
                  <h3 className="text-xl font-black text-white mt-1">
                    {product.title}
                  </h3>
                </div>

                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{product.duration}</span>
                </div>

                {/* Audio Preview Player */}
                <div className="pt-2">
                  <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Preview</p>
                  <audio
                    ref={(el) => audioRefs.current[product.id] = el}
                    controls
                    className="w-full h-10"
                    style={{
                      filter: 'invert(1) grayscale(1) contrast(0.9)',
                    }}
                    onPlay={() => setPlayingTrack(product.id)}
                    onPause={() => setPlayingTrack(null)}
                  >
                    <source src={product.previewPath} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>

                {/* Download Button */}
                <div className="pt-4 border-t border-gray-700">
                  <Button
                    onClick={() => handleDownload(product)}
                    className="w-full bg-white text-black hover:bg-gray-200 px-6 py-3 font-bold transition-all duration-300"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {product.isFree ? 'DOWNLOAD FREE' : 'DOWNLOAD MP3'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MusicStore;
